import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import type { ClientDto } from '@emag/shared'

export function ClientiPage() {
  const qc = useQueryClient()
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [cif, setCif] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await api.get<{ clients: ClientDto[] }>('/clients')).data.clients,
  })

  const create = useMutation({
    mutationFn: async () => (await api.post('/clients', { name, cif })).data,
    onSuccess: () => {
      toast.success('Client creat')
      setName('')
      setCif('')
      setShowForm(false)
      void qc.invalidateQueries({ queryKey: ['clients'] })
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    create.mutate()
  }

  const clients = data ?? []

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-b4l-text">Clienți</h1>
        {isAdmin && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="flex items-center gap-1.5 rounded-md bg-b4l-green px-3 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark"
          >
            <Plus size={15} />
            Client nou
          </button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={onSubmit}
          className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-b4l-border bg-white p-4"
        >
          <div>
            <label className="mb-1 block text-[12px] text-b4l-text-muted">Nume</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] text-b4l-text-muted">CIF</label>
            <input
              value={cif}
              onChange={(e) => setCif(e.target.value)}
              required
              className="rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
            />
          </div>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-md bg-b4l-green px-4 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60"
          >
            Salvează
          </button>
        </form>
      )}

      <div className="overflow-hidden rounded-xl border border-b4l-border bg-white">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-b4l-border text-left text-b4l-text-muted">
              <th className="px-4 py-2.5 font-medium">Nume</th>
              <th className="px-4 py-2.5 font-medium">CIF</th>
              <th className="px-4 py-2.5 font-medium">eMAG</th>
              <th className="px-4 py-2.5 font-medium">EA</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-b4l-text-dim">
                  Se încarcă…
                </td>
              </tr>
            )}
            {!isLoading && clients.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-b4l-text-dim">
                  Niciun client. {isAdmin ? 'Adaugă unul cu „Client nou".' : ''}
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-b4l-border last:border-0 hover:bg-b4l-bg-soft">
                <td className="px-4 py-2.5">
                  <Link to={`/clienti/${c.id}`} className="font-medium text-b4l-green-dark hover:underline">
                    {c.name}
                  </Link>
                </td>
                <td className="px-4 py-2.5">{c.cif}</td>
                <td className="px-4 py-2.5">{c.hasEmagConfig ? '✓' : '—'}</td>
                <td className="px-4 py-2.5">{c.hasEaConfig ? '✓' : '—'}</td>
                <td className="px-4 py-2.5">
                  <span
                    className={
                      c.active
                        ? 'rounded bg-b4l-green-light px-2 py-0.5 text-[11px] text-b4l-green-dark'
                        : 'rounded bg-b4l-bg px-2 py-0.5 text-[11px] text-b4l-text-dim'
                    }
                  >
                    {c.active ? 'activ' : 'inactiv'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
